import { useClient, useLiveQuery } from "@hq/client-react";
import type { ContentConflict } from "@hq/contracts";
import { Button, toast } from "@z/ui";
import { useState } from "react";
import { ConflictReview } from "./ConflictReview";
import { useAppConfig } from "../AppConfig";

import { useAccountSnapshot } from "../auth/useAccountSnapshot";
import { SignInForm } from "../auth/SignInForm";

export function AccountSettings() {
  const { account, rpc } = useAppConfig();
  const state = useAccountSnapshot(account);
  const client = useClient();
  const [busy, setBusy] = useState(false);
  const { data: conflicts = [] } = useLiveQuery(
    ["kv"],
    (s) => s.kv.get<ContentConflict[]>("sync:conflicts"),
    [],
  );
  if (!account) return null;
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } catch (error) {
      toast({
        title: "Could not complete request",
        description: error instanceof Error ? error.message : String(error),
        variant: "danger",
      });
    } finally {
      setBusy(false);
    }
  };
  const resolve = async (
    conflict: ContentConflict,
    resolution: "keep_server" | "keep_mine" | "keep_both",
  ) => {
    if (!rpc) return;
    await Promise.all([client.autosave.flushAll(), client.taskAutosave.flushAll()]);
    await client.sync.sync();
    if ((await client.store.mutations.pendingFor(conflict.entityId)).length)
      throw new Error("Sync pending changes before resolving this conflict");
    const entity =
      conflict.kind === "task"
        ? await client.store.tasks.get(conflict.entityId)
        : await client.store.notes.get(conflict.entityId);
    if (!entity) throw new Error("Sync this item before resolving its conflict");
    await rpc.conflicts.resolve({ id: conflict.id, resolution, baseRevision: entity.revision });
    await client.sync.pull();
  };
  return (
    <div className="space-y-3 pt-3">
      {state.phase === "signed_in" ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="mr-auto">{state.user?.email}</span>
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => void act(() => client.sync.sync())}
          >
            Sync now
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => void act(() => account.signOut())}
          >
            Sign out
          </Button>
        </div>
      ) : (
        <SignInForm account={account} />
      )}
      {state.phase === "signed_in" && state.error && (
        <p role="alert" className="text-sm text-danger">
          {state.error}
        </p>
      )}
      {conflicts.length > 0 && (
        <section aria-label="Sync conflicts" className="space-y-3">
          <h3 className="text-sm font-medium">Changes need your review ({conflicts.length})</h3>
          <p className="text-xs text-fg-muted">
            Both versions are preserved. Review the incoming text, then choose which to keep.
          </p>
          {conflicts.map((conflict) => (
            <ConflictReview
              key={conflict.id}
              conflict={conflict}
              disabled={busy || state.phase !== "signed_in"}
              onResolve={(resolution) => void act(() => resolve(conflict, resolution))}
            />
          ))}
        </section>
      )}
    </div>
  );
}
